import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Compass, Loader2, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError(t("auth.passwordTooShort")); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("auth.genericError"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg brand-bg flex items-center justify-center"><Compass className="h-5 w-5 text-white" /></div>
        <span className="font-display font-bold text-xl tracking-tight">{t("common.appName")}</span>
      </Link>
      <div className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold font-display tracking-tight">{t("auth.registerTitle")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("auth.registerSubtitle")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs">{t("auth.name")}</Label>
            <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label className="text-xs">{t("auth.email")}</Label>
            <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label className="text-xs">{t("auth.password")}</Label>
            <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete="new-password" />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
          <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full brand-bg text-white hover:opacity-90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.createAccount")}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-5 text-center">
          {t("auth.haveAccount")} <Link to="/login" className="brand-text font-medium">{t("common.login")}</Link>
        </p>
      </div>
      <p className="mt-6 text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> {t("auth.privacyNote")}</p>
    </div>
  );
}
